/**
 * Thin wrapper around fetch that attaches the bearer token from MSAL
 * and points at the configured API base URL.
 *
 * The .NET API uses Newtonsoft.Json with default PascalCase property names,
 * so we convert outgoing keys to PascalCase and incoming keys to camelCase.
 */

import { env } from '@/config/env';

const API_BASE_URL = env.VITE_API_BASE_URL;
const isDevelopment = import.meta.env.DEV;

let _getAccessToken: (() => Promise<string>) | null = null;

/** Called once at app startup from MsalProvider setup */
export function setTokenAcquirer(fn: () => Promise<string>): void {
  _getAccessToken = fn;
}

export function clearTokenAcquirer(): void {
  _getAccessToken = null;
}

/* ── Key-casing helpers ── */

function toPascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function toCamelCase(key: string): string {
  return key.charAt(0).toLowerCase() + key.slice(1);
}

function convertKeys(obj: unknown, convert: (k: string) => string): unknown {
  if (Array.isArray(obj)) return obj.map((item) => convertKeys(item, convert));
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[convert(key)] = convertKeys(value, convert);
    }
    return result;
  }
  return obj;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (_getAccessToken) {
    const token = await _getAccessToken();
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function buildApiUrl(path: string): string {
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
  return new URL(path.replace(/^\/+/, ''), baseUrl).toString();
}

function logApiRequest(method: string, url: string): void {
  if (isDevelopment) {
    console.info(`[api] ${method} ${url}`);
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = buildApiUrl(path);
  logApiRequest('GET', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API GET ${path} failed (${res.status}): ${text}`);
  }
  const json: unknown = await res.json();
  return convertKeys(json, toCamelCase) as T;
}

export async function apiPost<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API POST ${path} failed (${res.status}): ${text}`);
  }
  const json: unknown = await res.json();
  return convertKeys(json, toCamelCase) as TRes;
}

export async function apiPostNoContent<TReq>(path: string, body: TReq): Promise<void> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API POST ${path} failed (${res.status}): ${text}`);
  }
}

export async function apiPut<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const url = buildApiUrl(path);
  logApiRequest('PUT', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API PUT ${path} failed (${res.status}): ${text}`);
  }
  const json: unknown = await res.json();
  return convertKeys(json, toCamelCase) as TRes;
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const url = buildApiUrl(path);
  logApiRequest('GET', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API GET (blob) ${path} failed (${res.status}): ${text}`);
  }
  const arrayBuffer = await res.arrayBuffer();
  return new Blob([arrayBuffer], { type: res.headers.get('content-type') ?? '' });
}

export async function apiUploadFile(path: string, file: File): Promise<void> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers: Record<string, string> = {};
  if (_getAccessToken) {
    const token = await _getAccessToken();
    headers['Authorization'] = `Bearer ${token}`;
  }
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API UPLOAD ${path} failed (${res.status}): ${text}`);
  }
}
